import React from 'react';
import type { CostEstimateInfo } from '../../../../shared/types/epics';
import { StatusBadge, toneForConfidence } from '../ui/StatusBadge';

/**
 * FORGE-303 — calibrated cost estimation. Shows the estimate breakdown
 * (base $, correction factor, band, dominant confidence — FR-15) and, once
 * the epic completes, the actual-vs-quote delta + whether it landed in band
 * (FR-16). Mirrors PRTrackingCard's "renders nothing when there's nothing to
 * show" discipline — most epics either predate FORGE-303 or haven't had a
 * plan posted yet, and the card shouldn't imply a feature isn't working.
 */

const fmt = (n: number) => `$${n.toFixed(2)}`;

export const CostEstimateCard: React.FC<{ costEstimate?: CostEstimateInfo | null }> = ({ costEstimate }) => {
  if (!costEstimate) return null;
  // A $0–$0 quote carries no information (and reads like a broken feature).
  if (costEstimate.highUsd <= 0 && costEstimate.midUsd <= 0 && costEstimate.actualUsd == null) return null;

  const isCalibrated = costEstimate.bandSource === 'project-calibrated';
  // FORGE-350: the Parity Proof quote is priced per point on the scope, with
  // no bottom-up base and no per-story confidence — don't show either.
  const isParity = costEstimate.kind === 'parity';
  const showBase = isCalibrated || costEstimate.baseEstimateUsd > 0;
  const hasActual = costEstimate.actualUsd != null;
  // FORGE-397: a Parity Proof quote prices ONE run; compare it with that run's spend.
  const hasRun = isParity && costEstimate.runActualUsd != null;
  const runSince = costEstimate.runSince ? new Date(costEstimate.runSince) : null;

  return (
    <section
      data-testid="cost-estimate-card"
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-2"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-slate-800">{isParity ? 'Cost estimate — Parity Proof' : 'Cost estimate'}</h2>
        <StatusBadge tone={isCalibrated ? 'purple' : 'neutral'}>
          {isCalibrated ? 'Project-calibrated' : 'Fleet estimate'}
        </StatusBadge>
      </div>

      <div className="text-xs text-slate-700">
        <span className="font-medium">
          {fmt(costEstimate.lowUsd)} – {fmt(costEstimate.highUsd)}
        </span>{' '}
        <span className="text-slate-500">(midpoint {fmt(costEstimate.midUsd)})</span>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-slate-500">
        {isParity && costEstimate.points != null && (
          <span data-testid="cost-estimate-points">
            <span className="font-medium text-slate-700">{costEstimate.points}</span> points × {fmt(costEstimate.perPointUsd)}/point
          </span>
        )}
        {showBase && (
          <span>
            Base estimate <span className="font-medium text-slate-700">{fmt(costEstimate.baseEstimateUsd)}</span>
          </span>
        )}
        {isCalibrated && (
          <span>
            Correction <span className="font-medium text-slate-700">×{costEstimate.calibrationFactor.toFixed(2)}</span> from {costEstimate.calibrationN} prior epic{costEstimate.calibrationN === 1 ? '' : 's'}
          </span>
        )}
        {!isParity && <span className="inline-flex items-center gap-1">
          Confidence
          <StatusBadge tone={toneForConfidence(costEstimate.dominantConfidence)}>
            {costEstimate.dominantConfidence}
          </StatusBadge>
        </span>}
      </div>

      {isParity && (
        <p className="text-2xs text-slate-500">
          Priced on the Parity Proof scope (understanding + tests per area + baseline run). It is re-priced each time you change the scope on the GitHub issue.
        </p>
      )}
      {!isCalibrated && !isParity && (
        <p className="text-2xs text-slate-500">
          Cold start — this project doesn't have enough completed epics yet for a calibrated quote. Uses the fleet/repo-history estimate; the band will tighten as more epics complete.
        </p>
      )}

      {hasRun && (
        <div
          data-testid="cost-estimate-run-actual"
          className={`text-2xs rounded px-2 py-1.5 ${
            costEstimate.runWithinBand === false && (costEstimate.runDeltaPct ?? 0) > 0
              ? 'bg-warning-50 text-warning-700'
              : 'bg-slate-50 text-slate-700'
          }`}
        >
          This run so far: <span className="font-medium">{fmt(costEstimate.runActualUsd as number)}</span>
          {costEstimate.runDeltaPct != null && (
            <> ({costEstimate.runDeltaPct >= 0 ? '+' : ''}{costEstimate.runDeltaPct}% vs midpoint{costEstimate.runWithinBand ? ', within band' : costEstimate.runDeltaPct > 0 ? ', over band' : ''})</>
          )}
          {(costEstimate.runReworkUsd ?? 0) > 0 && (
            <> — {fmt(costEstimate.runReworkUsd as number)} of it on {costEstimate.runFailedRuns} failed or cut-off run{costEstimate.runFailedRuns === 1 ? '' : 's'}</>
          )}
          {runSince && !Number.isNaN(runSince.getTime()) && (
            <span className="text-slate-500"> · since {runSince.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
          )}
          <div className="text-slate-500 mt-0.5">
            Build cost below is the epic's lifetime total — every run and phase on this epic, including earlier runs and the migration assessment.
          </div>
        </div>
      )}

      {hasActual && (
        <div
          data-testid="cost-estimate-actual"
          className={`text-2xs rounded px-2 py-1.5 ${
            costEstimate.withinBand ? 'bg-success-50 text-success-700' : 'bg-warning-50 text-warning-700'
          }`}
        >
          Actual: <span className="font-medium">{fmt(costEstimate.actualUsd as number)}</span>
          {costEstimate.deltaPct != null && (
            <>
              {' '}
              ({costEstimate.deltaUsd != null && costEstimate.deltaUsd >= 0 ? '+' : ''}
              {costEstimate.deltaPct}% vs midpoint)
            </>
          )}
          {' — '}
          {costEstimate.withinBand ? 'within band ✅' : 'outside band ⚠️'}
        </div>
      )}
    </section>
  );
};

export default CostEstimateCard;
